import { BrowserModule } from "@angular/platform-browser";
import { Inject, Injector, NgModule } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { HttpClient } from "@angular/common/http";
import { ReactiveFormsModule } from "@angular/forms";
import { LayoutModule } from "@jeanologia/ng-layout";
import { HttpDataSource, HttpPaginationDataProxy } from "@jeanologia/ng-interfaces";
import { JeanologiaNgWidgetsModule } from "@jeanologia/ng-widgets";
import { JeanologiaNgSsoauthModule } from "@jeanologia/ng-ssoauth";
import { FormlyModule } from "@ngx-formly/core";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
//import { NgxCaptchaModule } from "ngx-captcha";
import { Observable } from "rxjs";
import {
	COUPONS_PATH,
	AssignedLicensesSource,
	AssignedLicensesProxy,
	CompaniesSource,
	CouponsSource,
	CouponsProxy,
} from "./features/models/constants/_constants";
import { LicenseDataProxy } from "./shared/classes/licenses.dataproxy";
import { UsersDataSource } from "./shared/classes/users.datasource";
import { CompaniesDataSource } from "./shared/classes/companies.datasource";
import { ProductsDataSource } from "./shared/classes/apps.datasource";
import { LicensesDataSource } from "./shared/classes/licenses.datasource";
import { CouponsDataProxy } from "./shared/classes/coupons.dataproxy";
import { CouponsDataSource } from "./shared/classes/coupons.datasource";
import { APP_CONFIG, ENVIRONMENT } from "./core/token";
import { AppComponent } from "./app.component";
import { DefaultLayoutComponent } from "./features/containers/default-layout/default-layout.component";
import { CoreModule } from "./core/core.module";
import { AppRoutingModule } from "./app-routing.module";
import { SharedModule } from "./shared/shared.module";
import { MaterialModule } from "./shared/utils/material/material.module";

export class CustomTranslateLoader implements TranslateLoader {
	public cdn_endpoint: string = "";

	constructor(@Inject(ENVIRONMENT) protected environment, private http: HttpClient) {
		this.cdn_endpoint = environment.cdnEndpoint;
	}

	getTranslation(lang: string): Observable<any> {
		const url = `${this.cdn_endpoint}assets/i18n/${lang}.json`;
		return this.http.get(url);
	}
}

export function createTranslateLoader(environment: any, http: HttpClient) {
	return new CustomTranslateLoader(environment, http);
}

@NgModule({
	declarations: [AppComponent, DefaultLayoutComponent],
	imports: [
		AppRoutingModule,
		BrowserAnimationsModule,
		BrowserModule,
		JeanologiaNgWidgetsModule,
		LayoutModule,
		MaterialModule,
		ReactiveFormsModule,
		//NgxCaptchaModule,
		SharedModule,
		JeanologiaNgSsoauthModule.forRootAsync({
			deps: [APP_CONFIG],
			useFactory: (app_config) => ({
				clientId: app_config.client_id,
				loginServer: app_config.login_server,
				api_url: app_config.api_url,
			}),
		}),
		CoreModule,
		FormlyModule.forRoot({ extras: { lazyRender: true } }),
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: createTranslateLoader,
				deps: [ENVIRONMENT, HttpClient],
			},
			fallbackLang: "en",
		}),
	],
	providers: [
		MatDialog,
		{ provide: "proxy", useClass: HttpPaginationDataProxy },
		{ provide: "clientsProxy", useClass: HttpPaginationDataProxy },
		{ provide: "toolsProxy", useClass: HttpPaginationDataProxy },
		{ provide: "salesProxy", useClass: HttpPaginationDataProxy },
		{ provide: "salesTrialProxy", useClass: HttpPaginationDataProxy },
		{ provide: "purchasedPackProxy", useClass: HttpPaginationDataProxy },
		{ provide: "customersProxy", useClass: HttpPaginationDataProxy },
		{ provide: "toolsService", useClass: HttpDataSource },
		{ provide: "salesService", useClass: HttpDataSource },
		{ provide: "salesTrialService", useClass: HttpDataSource },
		{ provide: "clientsService", useClass: HttpDataSource },
		{ provide: "customersService", useClass: HttpDataSource },
		{ provide: "service", useClass: HttpDataSource },
		{ provide: "paymentManagementsProxy", useClass: HttpPaginationDataProxy },
		{ provide: "paymentManagementsService", useClass: HttpDataSource },
		{ provide: COUPONS_PATH, useValue: COUPONS_PATH },
		{
			provide: CompaniesDataSource,
			useClass: CompaniesDataSource,
			deps: [ENVIRONMENT, HttpClient],
		},
		{
			provide: ProductsDataSource,
			useClass: ProductsDataSource,
			deps: [ENVIRONMENT, HttpClient],
		},
		{
			provide: UsersDataSource,
			useClass: UsersDataSource,
			deps: [ENVIRONMENT, HttpClient],
		},
		{
			provide: CompaniesSource,
			useClass: HttpDataSource,
			deps: [ENVIRONMENT, HttpClient],
		},
		{
			provide: CouponsSource,
			useClass: CouponsDataSource,
			deps: [ENVIRONMENT, HttpClient],
		},
		{
			provide: CouponsProxy,
			useClass: CouponsDataProxy,
			deps: [HttpClient, CompaniesDataSource, ProductsDataSource],
		},
		{
			provide: AssignedLicensesSource,
			useClass: LicensesDataSource,
			deps: [HttpClient],
		},
		{
			provide: AssignedLicensesProxy,
			useClass: LicenseDataProxy,
			deps: [Injector, HttpClient, UsersDataSource, CompaniesDataSource, ProductsDataSource],
		},
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
